import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import api, { safeRedirect, safeOpen } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Gem } from "lucide-react";

// Page Abonnement dédiée — plan, usage, code promo, portail de facturation.
// Extraite de Settings pour être accessible directement depuis la sidebar (owners).
export default function Billing() {
  const { brand, activeCompany, role, fetchCompanies } = useAuth();

  const [usage, setUsage] = useState(null); // { proposals:{used,max}, users, ai, plan }
  const [plans, setPlans] = useState([]); // forfaits publics (depuis l'API)
  const [subscribing, setSubscribing] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [billingPortalLoading, setBillingPortalLoading] = useState(false);

  useEffect(() => {
    if (!activeCompany) return;
    api.get(`/companies/${activeCompany}/usage`).then((r) => setUsage(r.data)).catch(() => setUsage(null));
  }, [activeCompany]);

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => setPlans([]));
  }, []);

  const openBillingPortal = async () => {
    setBillingPortalLoading(true);
    try {
      const { data } = await api.post(`/companies/${activeCompany}/billing-portal`);
      safeOpen(data.url);
    } catch (e) { toast.error("Portail de facturation non disponible. Configurez Stripe dans les paramètres."); }
    finally { setBillingPortalLoading(false); }
  };

  const redeemPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const { data } = await api.post(`/companies/${activeCompany}/redeem-promo`, { code: promoCode.trim() });
      toast.success(data.message || "Code promo appliqué !");
      setPromoCode("");
      fetchCompanies();
    } catch (e) {
      toast.error(e.response?.data?.message || "Code promo invalide.");
    } finally {
      setPromoLoading(false);
    }
  };

  const subscribePlan = async (plan, interval = 'month') => {
    setSubscribing(plan);
    try {
      const { data } = await api.post(`/companies/${activeCompany}/subscribe`, { plan, interval });
      safeRedirect(data.url);
    } catch (e) { toast.error(e.response?.data?.message || "Stripe non configuré pour cette société."); }
    finally { setSubscribing(null); }
  };

  // Abonnement réservé au propriétaire de la société.
  if (role !== 'owner') return <Navigate to="/dashboard" replace />;

  return (
    <div className="p-6 md:p-8 lg:p-12 max-w-4xl">
      <div className="mb-8">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-2">Société · {brand.name}</p>
        <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">Abonnement</h1>
      </div>

      <div className="bg-card border border-border rounded-sm p-6 lg:p-8 space-y-5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Gem className="w-5 h-5 text-primary" />
            <h3 className="font-heading text-lg font-semibold">Votre forfait</h3>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] tracking-wide uppercase px-2 py-1 rounded-sm font-semibold border ${
              brand.subscription_status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
              brand.subscription_status === 'trialing' ? 'bg-blue-50 text-blue-700 border-blue-200' :
              brand.subscription_status === 'past_due' ? 'bg-amber-50 text-amber-700 border-amber-200' :
              brand.subscription_status === 'canceled' ? 'bg-red-50 text-red-700 border-red-200' :
              'bg-muted text-muted-foreground border-transparent'
            }`}>
              {brand.subscription_status === 'active' ? 'Actif' :
               brand.subscription_status === 'trialing' ? 'Essai' :
               brand.subscription_status === 'past_due' ? 'Paiement en retard' :
               brand.subscription_status === 'canceled' ? 'Annulé' : 'Gratuit'}
            </span>
            <span className="text-sm font-semibold capitalize">{brand.plan || 'starter'}</span>
          </div>
        </div>

        {brand.current_period_end && (
          <p className="text-sm text-muted-foreground">
            Période en cours jusqu'au <span className="font-medium text-foreground">{new Date(brand.current_period_end).toLocaleDateString('fr-FR')}</span>
          </p>
        )}

        {/* Usage vs limites du forfait */}
        {usage && (
          <div className="grid sm:grid-cols-3 gap-4" data-testid="plan-usage">
            {[
              { key: 'proposals', label: 'Propositions', u: usage.proposals },
              { key: 'users', label: 'Utilisateurs', u: usage.users },
              { key: 'ai', label: 'Générations IA (ce mois)', u: usage.ai },
            ].map(({ key, label, u }) => {
              const unlimited = u.max === -1;
              const pct = unlimited ? 0 : Math.min(100, Math.round((u.used / Math.max(u.max, 1)) * 100));
              const atLimit = !unlimited && u.used >= u.max;
              return (
                <div key={key} className="border border-border rounded-sm p-3">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="text-sm font-semibold mt-0.5 tabular-nums">
                    {u.used} <span className="text-muted-foreground font-normal">/ {unlimited ? '∞' : u.max}</span>
                  </p>
                  {!unlimited && (
                    <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${atLimit ? 'bg-destructive' : 'bg-primary'}`} style={{ width: `${pct}%` }} />
                    </div>
                  )}
                  {atLimit && <p className="text-[11px] text-destructive mt-1.5">Limite atteinte — passez à un forfait supérieur.</p>}
                </div>
              );
            })}
          </div>
        )}

        {/* Cartes de plans (depuis l'API — forfaits pilotés par le super-admin) */}
        {plans.length > 0 && (
          <div className="grid sm:grid-cols-3 gap-4">
            {plans.map(plan => {
              const isCurrent = (brand.plan || 'starter') === plan.tier;
              return (
                <div key={plan.tier} className={`border rounded-sm p-4 space-y-3 ${isCurrent ? 'border-primary bg-primary/5' : 'border-border'}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-heading font-bold">{plan.label}</span>
                    {isCurrent && <span className="text-[10px] bg-primary text-white px-1.5 py-0.5 rounded-sm font-semibold">Actuel</span>}
                  </div>
                  <p className="text-lg font-bold">{plan.priceLabel || '—'}</p>
                  {plan.trialDays > 0 && <p className="text-[11px] text-emerald-700 font-medium">{plan.trialDays} jours d'essai offerts</p>}
                  <ul className="space-y-1">
                    {(plan.features || []).map(f => <li key={f} className="text-xs text-muted-foreground flex items-start gap-1"><span className="text-primary mt-0.5">✓</span>{f}</li>)}
                  </ul>
                  {!isCurrent && (
                    <button onClick={() => subscribePlan(plan.tier)}
                      disabled={subscribing === plan.tier}
                      className="w-full text-xs py-2 rounded-sm bg-primary text-white font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors">
                      {subscribing === plan.tier ? 'Redirection…' : plan.tier === 'enterprise' ? 'Nous contacter' : `Passer à ${plan.label}`}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Code promo */}
        <div className="pt-4 border-t border-border space-y-2">
          <p className="text-sm font-medium">Code promo</p>
          <div className="flex gap-2">
            <Input
              value={promoCode}
              onChange={e => setPromoCode(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === 'Enter' && redeemPromo()}
              placeholder="ACTEIO2026"
              className="rounded-sm font-mono uppercase text-sm max-w-[200px]"
            />
            <Button
              size="sm"
              variant="outline"
              className="rounded-sm"
              onClick={redeemPromo}
              disabled={promoLoading || !promoCode.trim()}
            >
              {promoLoading ? "Application…" : "Appliquer"}
            </Button>
          </div>
        </div>

        {/* Bouton portail facturation */}
        {brand.stripe_customer_id_set && (
          <div className="pt-3 border-t border-border">
            <button onClick={openBillingPortal} disabled={billingPortalLoading}
              className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors disabled:opacity-50">
              {billingPortalLoading ? 'Ouverture…' : "Gérer l'abonnement, les factures et les moyens de paiement →"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
